import { create, StateCreator } from "zustand";
import {
  createJSONStorage,
  devtools,
  persist,
  StateStorage,
} from "zustand/middleware";
import { get, set, del } from "idb-keyval";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import {
  WorkflowImageI,
  WorkflowTwoActionI,
  WorkflowTwoI,
} from "../../../../../../libs/interfaces";
import { removeImageBlob, saveImageBlob } from "../../../../../../libs/helpers";

// Custom storage object
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    return (await get(name)) || null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await set(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    await del(name);
  },
};

export const createWorkflowSlice: StateCreator<
  WorkflowTwoI & WorkflowTwoActionI,
  [],
  []
> = (set, get) => ({
  id: null,
  container: null,
  coordinates: "",
  client: null,
  clientId: null,
  clientIdentification: null,
  plateVehicle: "",
  driverName: "",
  driverIdentification: "",
  product: "",
  presentation: "",
  palletNumber: "",
  presentationNumber: "",
  samplingNumber: "",
  extraFields: [],
  hourInit: "",
  hourEnd: "",
  startProcess: "",
  endProcess: "",
  address: "",
  city: "",
  typeReview: "",
  storageName: "",
  createdByAdmin: null,
  quantityImages: 8,
  images: [],
  imagesDelete: [],
  companyTransport: "",
  naviera: "",
  typeContainer: "",
  size: null,
  observation: null,
  openedBy: "",
  openedWas: "No",
  //FOTOS VACIO EXTERNO
  //FOTOS CARGA
  chargingProcessPhoto1: "",
  chargingProcessComment1: "",
  chargingProcessPhoto2: "",
  chargingProcessComment2: "",
  chargingProcessVideo: "",
  //FOTOS CONTENEDOR
  containerPanoramicPhoto: "",
  containerPanoramicComment: "",
  containerPanoramicCoordinates: "",
  navieraBottlePhoto: "",
  navieraBottleComment: "",
  navieraWirePhoto: "",
  navieraWireComment: "",
  navieraLabelPhoto: "",
  navieraLabelComment: "",
  exporterBottlePhoto: "",
  exporterBottleComment: "",
  exporterWirePhoto: "",
  exporterWireComment: "",
  exporterLabelPhoto: "",
  exporterLabelComment: "",
  otherBottlePhoto: "",
  otherBottleComment: "",
  otherWirePhoto: "",
  otherWireComment: "",
  otherLabelPhoto: "",
  otherLabelComment: "",
  gpsPhoto: "",
  gpsComment: "",
  gpsStampPhoto: "",
  gpsStampComment: "",
  entryPort: "",
  exitDoorVideo: "",
  exitEngineryVideo: "",

  setCoordinates: (coordinates) => {
    set(() => ({ coordinates }));
  },
  setCompanyTransport: (companyTransport) => set(() => ({ companyTransport })),
  setObservation: (observation) => set(() => ({ observation })),
  setNaviera: (naviera) => set(() => ({ naviera })),
  setTypeContainer: (typeContainer) => set(() => ({ typeContainer })),
  setSize: (size) => set(() => ({ size })),
  setCreatedByAdmin: (createdByAdmin) => set(() => ({ createdByAdmin })),
  setOpenedBy: (openedBy) => {
    set(() => ({ openedBy }));
  },
  setOpenedWas: (openedWas) => {
    set(() => ({ openedWas }));
  },

  setChargingProcessPhoto1: async (file) => {
    const getImageBack = get().chargingProcessPhoto1;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ chargingProcessPhoto1: photoId }));
  },
  setChargingProcessComment1: (chargingProcessComment1) =>
    set(() => ({ chargingProcessComment1 })),
  setChargingProcessPhoto2: async (file) => {
    const getImageBack = get().chargingProcessPhoto2;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ chargingProcessPhoto2: photoId }));
  },
  setChargingProcessComment2: (chargingProcessComment2) =>
    set(() => ({ chargingProcessComment2 })),
  setChargingProcessVideo: async (file) => {
    if (!file) {
      set(() => ({ chargingProcessVideo: "" }));
      return;
    }
    set(() => ({ chargingProcessVideo: file as string }));
  },

  setContainerPanoramicPhoto: async (file) => {
    const getImageBack = get().containerPanoramicPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    const coordinates = get().coordinates;
    set(() => ({
      containerPanoramicPhoto: photoId,
      containerPanoramicCoordinates: coordinates,
    }));
  },
  setContainerPanoramicComment: (containerPanoramicComment) =>
    set(() => ({ containerPanoramicComment })),
  setContainerPanoramicCoordinates: (containerPanoramicCoordinates) =>
    set(() => ({ containerPanoramicCoordinates })),

  setNavieraBottlePhoto: async (file) => {
    const getImageBack = get().navieraBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraBottlePhoto: photoId }));
  },
  setNavieraBottleComment: (navieraBottleComment) =>
    set(() => ({ navieraBottleComment })),
  setNavieraWirePhoto: async (file) => {
    const getImageBack = get().navieraWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraWirePhoto: photoId }));
  },
  setNavieraWireComment: (navieraWireComment) =>
    set(() => ({ navieraWireComment })),
  setNavieraLabelPhoto: async (file) => {
    const getImageBack = get().navieraLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraLabelPhoto: photoId }));
  },
  setNavieraLabelComment: (navieraLabelComment) =>
    set(() => ({ navieraLabelComment })),

  setExporterBottlePhoto: async (file) => {
    const getImageBack = get().exporterBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterBottlePhoto: photoId }));
  },
  setExporterBottleComment: (exporterBottleComment) =>
    set(() => ({ exporterBottleComment })),
  setExporterWirePhoto: async (file) => {
    const getImageBack = get().exporterWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterWirePhoto: photoId }));
  },
  setExporterWireComment: (exporterWireComment) =>
    set(() => ({ exporterWireComment })),
  setExporterLabelPhoto: async (file) => {
    const getImageBack = get().exporterLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterLabelPhoto: photoId }));
  },
  setExporterLabelComment: (exporterLabelComment) =>
    set(() => ({ exporterLabelComment })),

  setOtherBottlePhoto: async (file) => {
    const getImageBack = get().otherBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherBottlePhoto: photoId }));
  },
  setOtherBottleComment: (otherBottleComment) =>
    set(() => ({ otherBottleComment })),
  setOtherWirePhoto: async (file) => {
    const getImageBack = get().otherWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherWirePhoto: photoId }));
  },
  setOtherWireComment: (otherWireComment) => set(() => ({ otherWireComment })),
  setOtherLabelPhoto: async (file) => {
    const getImageBack = get().otherLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherLabelPhoto: photoId }));
  },
  setOtherLabelComment: (otherLabelComment) =>
    set(() => ({ otherLabelComment })),

  setGpsPhoto: async (file) => {
    const getImageBack = get().gpsPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ gpsPhoto: photoId }));
  },
  setGpsComment: (gpsComment) => set(() => ({ gpsComment })),
  setGpsStampPhoto: async (file) => {
    const getImageBack = get().gpsStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ gpsStampPhoto: photoId }));
  },
  setGpsStampComment: (gpsStampComment) => set(() => ({ gpsStampComment })),
  setEntryPort: (entryPort) => set(() => ({ entryPort })),
  setExitDoorVideo: async (file) => {
    if (!file) {
      set(() => ({ exitDoorVideo: "" }));
      return;
    }
    set(() => ({ exitDoorVideo: file as string }));
  },
  setExitEngineryVideo: async (file) => {
    if (!file) {
      set(() => ({ exitEngineryVideo: "" }));
      return;
    }
    set(() => ({ exitEngineryVideo: file as string }));
  },

  setId: (id) => set(() => ({ id })),
  setHourInit: (hourInit) => set(() => ({ hourInit })),
  setHourEnd: (hourEnd) => set(() => ({ hourEnd })),
  setStartProcess: (startProcess) => set(() => ({ startProcess })),
  setEndProcess: (endProcess) => set(() => ({ endProcess })),
  setAddress: (address) => set(() => ({ address })),
  setCity: (city) => set(() => ({ city })),
  setTypeReview: (typeReview) => set(() => ({ typeReview })),
  setStorageName: (storageName) => set(() => ({ storageName })),

  setContainer: (container) => {
    if (get().container !== "" || get().container !== null) {
      const now = moment();
      const hourGenerate = now.format("HH:mm:ss");
      set(() => ({ container, hourInit: hourGenerate }));
    } else {
      set(() => ({ container }));
    }
  },
  setClient: (client) => set(() => ({ client })),
  setClientId: (clientId) => set(() => ({ clientId })),
  setClientIdentification: (clientIdentification) =>
    set(() => ({ clientIdentification })),
  setPlateVehicle: (plateVehicle) => set(() => ({ plateVehicle })),
  setDriverName: (driverName) => set(() => ({ driverName })),
  setProduct: (product) => set(() => ({ product })),
  setPresentation: (presentation) => set(() => ({ presentation })),
  setPalletNumber: (palletNumber) => set(() => ({ palletNumber })),
  setPresentationNumber: (presentationNumber) =>
    set(() => ({ presentationNumber })),
  setSamplingNumber: (samplingNumber) => set(() => ({ samplingNumber })),
  setExtraFields: (extraFields) => set(() => ({ extraFields })),
  setDriverIdentification: (driverIdentification) =>
    set(() => ({ driverIdentification })),
  setImages: (images) => set(() => ({ images })),
  getOneImage: (uuid) => {
    const items_ = get().images;
    return items_.find((el) => el.uuid === uuid)!;
  },
  addImage: (type: any, section?: string, name?: string) => {
    set((state) => ({
      images: [
        ...state.images,
        {
          id: null,
          src: "",
          comment: "",
          name,
          uuid: uuidv4(),
          model: "wf-two",
          type,
          section,
          order: state.images.length + 1,
        },
      ],
    }));
  },
  addImageDelete: (id) => set(() => ({ id: null })),
  removeImage: async (uuid) => {
    const items_ = get().images;
    const itemsDelete_ = get().imagesDelete!;
    const fill = items_.find((el) => el.uuid === uuid);
    const arrnew = items_.filter((el) => el.uuid !== uuid);

    if (fill) await removeImageBlob(fill.src);

    if (fill!.id && !itemsDelete_.includes(fill!.id))
      itemsDelete_.push(fill!.id);

    set(() => ({
      images: arrnew,
      imagesDelete: itemsDelete_,
    }));
  },
  updateImage: async (uuid, field, value) => {
    let laPhoto = "";

    if (uuid && field === "src") {
      await removeImageBlob(uuid);
      const photoId = crypto.randomUUID();

      const blob = await (await fetch(value)).blob();
      await saveImageBlob(photoId, blob);

      laPhoto = photoId;
    }

    set((state) => {
      const items_ = state.images;
      const arrnew: WorkflowImageI[] = [];

      for (const x of items_) {
        if (x.uuid === uuid) {
          const obj = {
            ...x,
            [field]: field === "src" ? laPhoto : value,
          };
          arrnew.push(obj);
        } else {
          arrnew.push(x);
        }
      }
      return {
        images: arrnew,
      };
    });
  },
  getOnlyPhotos: () => ({
    chargingProcessPhoto1: get().chargingProcessPhoto1,
    chargingProcessPhoto2: get().chargingProcessPhoto2,
    containerPanoramicPhoto: get().containerPanoramicPhoto,
    navieraBottlePhoto: get().navieraBottlePhoto,
    navieraWirePhoto: get().navieraWirePhoto,
    navieraLabelPhoto: get().navieraLabelPhoto,
    exporterBottlePhoto: get().exporterBottlePhoto,
    exporterWirePhoto: get().exporterWirePhoto,
    exporterLabelPhoto: get().exporterLabelPhoto,
    otherBottlePhoto: get().otherBottlePhoto,
    otherWirePhoto: get().otherWirePhoto,
    otherLabelPhoto: get().otherLabelPhoto,
    gpsPhoto: get().gpsPhoto,
    gpsStampPhoto: get().gpsStampPhoto,
  }),
  getValues: () => ({
    container: get().container,
    coordinates: get().coordinates,
    createdByAdmin: get().createdByAdmin,
    observation: get().observation,
    clientId: get().clientId,
    client: get().client,
    plateVehicle: get().plateVehicle,
    driverName: get().driverName,
    driverIdentification: get().driverIdentification,
    product: get().product,
    presentation: get().presentation,
    palletNumber: get().palletNumber,
    presentationNumber: get().presentationNumber,
    samplingNumber: get().samplingNumber,
    extraFields: get().extraFields,
    openedBy: get().openedBy,
    openedWas: get().openedWas,
    hourInit: get().hourInit,
    hourEnd: get().hourEnd,
    startProcess: get().startProcess,
    endProcess: get().endProcess,
    address: get().address,
    city: get().city,
    typeReview: get().typeReview,
    storageName: get().storageName,
    images: get().images,
    imagesDelete: get().imagesDelete,
    chargingProcessPhoto1: get().chargingProcessPhoto1,
    chargingProcessComment1: get().chargingProcessComment1,
    chargingProcessPhoto2: get().chargingProcessPhoto2,
    chargingProcessComment2: get().chargingProcessComment2,
    chargingProcessVideo: get().chargingProcessVideo,
    containerPanoramicPhoto: get().containerPanoramicPhoto,
    containerPanoramicComment: get().containerPanoramicComment,
    containerPanoramicCoordinates: get().containerPanoramicCoordinates,
    navieraBottlePhoto: get().navieraBottlePhoto,
    navieraBottleComment: get().navieraBottleComment,
    navieraWirePhoto: get().navieraWirePhoto,
    navieraWireComment: get().navieraWireComment,
    navieraLabelPhoto: get().navieraLabelPhoto,
    navieraLabelComment: get().navieraLabelComment,
    exporterBottlePhoto: get().exporterBottlePhoto,
    exporterBottleComment: get().exporterBottleComment,
    exporterWirePhoto: get().exporterWirePhoto,
    exporterWireComment: get().exporterWireComment,
    exporterLabelPhoto: get().exporterLabelPhoto,
    exporterLabelComment: get().exporterLabelComment,
    otherBottlePhoto: get().otherBottlePhoto,
    otherBottleComment: get().otherBottleComment,
    otherWirePhoto: get().otherWirePhoto,
    otherWireComment: get().otherWireComment,
    otherLabelPhoto: get().otherLabelPhoto,
    otherLabelComment: get().otherLabelComment,
    gpsPhoto: get().gpsPhoto,
    gpsComment: get().gpsComment,
    gpsStampPhoto: get().gpsStampPhoto,
    gpsStampComment: get().gpsStampComment,
    entryPort: get().entryPort,
    exitDoorVideo: get().exitDoorVideo,
    exitEngineryVideo: get().exitEngineryVideo,
    companyTransport: get().companyTransport,
    naviera: get().naviera,
    typeContainer: get().typeContainer,
    size: get().size,
  }),
  setValues: (ladata) => {
    set(ladata);
    set(() => ({ imagesDelete: [] }));
  },
  getImages: () => get().images,
  onClear: async () => {
    const state = get();
    const keys = [
      state.chargingProcessPhoto1,
      state.chargingProcessPhoto2,
      state.chargingProcessVideo,
      state.containerPanoramicPhoto,
      state.navieraBottlePhoto,
      state.navieraWirePhoto,
      state.navieraLabelPhoto,
      state.exporterBottlePhoto,
      state.exporterWirePhoto,
      state.exporterLabelPhoto,
      state.otherBottlePhoto,
      state.otherWirePhoto,
      state.otherLabelPhoto,
      state.gpsPhoto,
      state.gpsStampPhoto,
      state.exitDoorVideo,
      state.exitEngineryVideo,
    ];

    for (const key of keys) {
      if (
        key &&
        !key.startsWith("http://") &&
        !key.startsWith("https://") &&
        !key.startsWith("data:")
      ) {
        await removeImageBlob(key);
      }
    }

    const images_ = get().images;
    for (const x of images_) {
      if (
        x.src.startsWith("http://") ||
        x.src.startsWith("https://") ||
        x.src.startsWith("data:")
      )
        continue;
      await removeImageBlob(x.src);
    }

    set(() => ({
      id: null,
      container: null,
      createdByAdmin: null,
      client: null,
      observation: null,
      openedBy: "",
      openedWas: "No",
      plateVehicle: "",
      driverName: "",
      driverIdentification: "",
      product: "",
      presentation: "",
      palletNumber: "",
      presentationNumber: "",
      samplingNumber: "",
      extraFields: [],
      hourInit: "",
      hourEnd: "",
      startProcess: "",
      endProcess: "",
      address: "",
      city: "",
      typeReview: "",
      storageName: "",
      images: [],
      imagesDelete: [],
      step: "Formulario",
      companyTransport: "",
      naviera: "",
      typeContainer: "",
      size: null,
      chargingProcessPhoto1: "",
      chargingProcessComment1: "",
      chargingProcessPhoto2: "",
      chargingProcessComment2: "",
      chargingProcessVideo: "",
      containerPanoramicPhoto: "",
      containerPanoramicComment: "",
      containerPanoramicCoordinates: "",
      navieraBottlePhoto: "",
      navieraBottleComment: "",
      navieraWirePhoto: "",
      navieraWireComment: "",
      navieraLabelPhoto: "",
      navieraLabelComment: "",
      exporterBottlePhoto: "",
      exporterBottleComment: "",
      exporterWirePhoto: "",
      exporterWireComment: "",
      exporterLabelPhoto: "",
      exporterLabelComment: "",
      otherBottlePhoto: "",
      otherBottleComment: "",
      otherWirePhoto: "",
      otherWireComment: "",
      otherLabelPhoto: "",
      otherLabelComment: "",
      gpsPhoto: "",
      gpsComment: "",
      gpsStampPhoto: "",
      gpsStampComment: "",
      entryPort: "",
      exitDoorVideo: "",
      exitEngineryVideo: "",
    }));
  },
  step: "Formulario",
  setStep: (step) => set(() => ({ step })),
  resetSteps: () =>
    set(() => ({
      step: "Formulario",
    })),
});

export const useWorkflowTwoStore = create<WorkflowTwoI & WorkflowTwoActionI>()(
  devtools(
    persist(
      (...a) => ({
        ...createWorkflowSlice(...a),
      }),
      {
        name: "workflow-two-ecupro",
        storage: createJSONStorage(() => storage),
      }
    )
  )
);